import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Flame, Brain, Bug } from "@phosphor-icons/react";

/** Compact reactions row — 3 buttons that toggle 🔥 / 🧠 / 🐞 reactions on any target.
 * Props:
 *   - targetId (required): id of the question/answer/post/comment to react to
 *   - initial (optional): pre-fetched summary `{fire:n, brain:n, bug:n, mine:[...]}` to skip the network round-trip
 *   - size: "sm" (default) | "md"
 */
const KINDS = [
    { key: "fire",  Icon: Flame, label: "Fire", color: "text-rose-400",     active: "text-rose-300 bg-rose-500/10 border-rose-500/40" },
    { key: "brain", Icon: Brain, label: "Insightful", color: "text-amber-400", active: "text-amber-300 bg-amber-500/10 border-amber-500/40" },
    { key: "bug",   Icon: Bug,   label: "Confirmed", color: "text-emerald-400", active: "text-emerald-300 bg-emerald-500/10 border-emerald-500/40" },
];

export default function Reactions({ targetId, initial = null, size = "sm" }) {
    const { user } = useAuth();
    const [data, setData] = useState(initial || { fire: 0, brain: 0, bug: 0, mine: [] });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!targetId) return;
        if (initial) { setData(initial); return; }
        let alive = true;
        api.get(`/reactions/${targetId}`)
            .then(({ data }) => { if (alive) setData(data); })
            .catch(() => {});
        return () => { alive = false; };
    }, [targetId, initial]);

    const toggle = async (kind) => {
        if (!user) return toast.error("Sign in to react");
        if (busy) return;
        setBusy(true);
        const has = data.mine?.includes(kind);
        // Optimistic update
        setData((d) => ({
            ...d,
            [kind]: Math.max(0, (d[kind] || 0) + (has ? -1 : 1)),
            mine: has ? d.mine.filter((m) => m !== kind) : [...(d.mine || []), kind],
        }));
        try {
            if (has) {
                await api.delete(`/reactions/${targetId}`, { params: { reaction: kind } });
            } else {
                await api.post(`/reactions/${targetId}`, { reaction: kind });
            }
        } catch (e) {
            toast.error(formatErr(e));
            // Re-fetch on failure to reconcile
            try { const { data } = await api.get(`/reactions/${targetId}`); setData(data); } catch { /* noop */ }
        }
        setBusy(false);
    };

    const padding = size === "md" ? "px-2.5 py-1" : "px-1.5 py-0.5";
    const iconSize = size === "md" ? 14 : 12;
    const textSize = size === "md" ? "text-xs" : "text-[10px]";

    return (
        <div className="flex items-center gap-1" data-testid={`reactions-${targetId}`}>
            {KINDS.map(({ key, Icon, label, color, active }) => {
                const has = data.mine?.includes(key);
                const count = data[key] || 0;
                return (
                    <button
                        key={key}
                        type="button"
                        onClick={() => toggle(key)}
                        title={label}
                        data-testid={`reaction-${key}-${targetId}`}
                        className={`${padding} ${textSize} flex items-center gap-1 border rounded-sm font-mono transition-colors ${
                            has ? active : `border-white/10 ${color}/70 hover:bg-slate-800/40 hover:border-slate-700`
                        }`}
                    >
                        <Icon size={iconSize} weight={has ? "fill" : "regular"} />
                        {count > 0 && <span>{count}</span>}
                    </button>
                );
            })}
        </div>
    );
}
